import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fromActionPrefixId } from "features/Resources/Shared/helpers";
import { PrefixId } from "features/Resources/Shared/models";
import { v2ApiPendingNewHireJourneyIssuesPathById } from "helpers/url/api/v2";
import { apiPendingNewHireJourneysPath } from "helpers/url/pages/v1";
import { IssueSummary } from "models/issue";
import { NewHireJourneyId } from "models/newHire";
import apiClient from "services/ApiClient";
import { createQueryString } from "helpers/queryString";
import { generatePath } from "react-router";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { actionIdObjectFromPrefixId } from "./helpers";

export const pendingNewHireKeys = {
  all: ["pendingNewHires"] as const,
  lists: () => [...pendingNewHireKeys.all, "list"] as const,
  list: (filters: string) => [...pendingNewHireKeys.lists(), { filters }] as const,
  issues: () => [...pendingNewHireKeys.all, "issues"] as const,
  issuesById: (newHireJourneyid: NewHireJourneyId) => [...pendingNewHireKeys.lists(), newHireJourneyid] as const,
  byActions: () => [...pendingNewHireKeys.all, "actions"] as const,
  byActionId: (actionId: string) => [...pendingNewHireKeys.byActions(), actionId] as const,
  count: () => [...pendingNewHireKeys.all, "count"] as const,
};

export const usePendingNewHireJourneysListQuery = (options: { enabled?: boolean } = {}) => useQuery({
  queryKey: pendingNewHireKeys.lists(),
  queryFn: async () => apiClient.get(apiPendingNewHireJourneysPath()),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
});

export const usePendingNewHireJourneysCountQuery = (options: { enabled?: boolean } = {}) => useQuery<{ count: number }>({
  queryKey: pendingNewHireKeys.count(),
  queryFn: async () => apiClient.get(API_ROUTES.newHireJourneys.pending.count),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
});

export const usePendingNewHireJourneyIssuesQuery = (pendingNewHireId: NewHireJourneyId, options: { enabled?: boolean } = {}) => {
  const mergeEnabled = options?.enabled !== undefined ? options.enabled && !!pendingNewHireId : !!pendingNewHireId;
  return useQuery<IssueSummary[]>({
    queryKey: pendingNewHireKeys.issuesById(pendingNewHireId),
    queryFn: async () => apiClient.get(v2ApiPendingNewHireJourneyIssuesPathById(pendingNewHireId)),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
    enabled: mergeEnabled,
  });
};

export const useDeletePendingNewHireJourney = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { journeyId: NewHireJourneyId; destroyUser?: boolean }) => {
      const path = generatePath(API_ROUTES.newHireJourneys.show, { id: String(variables.journeyId) });
      const url = variables.destroyUser ? `${path}?destroy_user=true` : path;
      return apiClient.delete(url);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pendingNewHireKeys.all });
    },
  });
};

export const usePendingIssuesByActionPrefixIdQuery = (prefixId: PrefixId, options: { enabled?: boolean } = {}) => {
  const mergeEnabled = options?.enabled !== undefined ? options.enabled && !!prefixId : !!prefixId;
  const actionArgs = actionIdObjectFromPrefixId(prefixId);
  const { id } = fromActionPrefixId(prefixId);
  const query = createQueryString({ additionalQueries: actionArgs });
  return useQuery<Issue[]>({
    queryKey: pendingNewHireKeys.byActionId(id as string),
    queryFn: async () => apiClient.get(`${generatePath(API_ROUTES.pendingIssues.base)}?${query}`),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
    enabled: mergeEnabled,
  });
};
